/**
 * Gets the computed root font size from the document element.
 * Falls back to 16px if unable to determine (SSR or document not available).
 *
 * @returns The root font size in pixels
 */
export const getRootFontSize = (): number => {
  if (typeof window === "undefined" || !document?.documentElement) {
    return 16; // Default fallback for SSR
  }

  const computedStyle = window.getComputedStyle(document.documentElement);
  const fontSize = parseFloat(computedStyle.fontSize);

  return isNaN(fontSize) ? 16 : fontSize;
};

/**
 * Converts various dimension formats (from standard <img> tag props) to integer values
 * suitable for Next.js <Image> component.
 *
 * Supported input formats:
 * - number: 100 -> 100
 * - float: 10.5 -> 10
 * - string with px: "100px" -> 100
 * - string with rem: "10rem" -> converted to px (uses actual root font size from DOM)
 * - string with em: "10em" -> converted to px (uses actual root font size from DOM)
 * - string with %: "50%" -> undefined (percentage not supported)
 * - string number: "100" -> 100
 *
 * @param dimension - The dimension value from img tag props
 * @param baseFontSize - Base font size in pixels for rem/em conversion (default: auto-detected from DOM, fallback 16px)
 * @returns Integer value or undefined if conversion is not possible
 */
export const convertDimensionToInt = (
  dimension: string | number | undefined | null,
  baseFontSize?: number
): number | undefined => {
  // Get the base font size if not provided
  const resolvedBaseFontSize = baseFontSize ?? getRootFontSize();

  // Handle undefined or null
  if (dimension === undefined || dimension === null) {
    return undefined;
  }

  // Handle numeric values
  if (typeof dimension === "number") {
    // Convert to integer, rounding down
    return Math.floor(dimension);
  }

  // Handle string values
  if (typeof dimension === "string") {
    const trimmed = dimension.trim();

    // Handle empty string
    if (trimmed === "") {
      return undefined;
    }

    // Handle px values
    if (trimmed.endsWith("px")) {
      const value = parseFloat(trimmed);
      return isNaN(value) ? undefined : Math.floor(value);
    }

    // Handle rem/em values (relative to base font size)
    if (trimmed.endsWith("rem") || trimmed.endsWith("em")) {
      const value = parseFloat(trimmed);
      return isNaN(value) ? undefined : Math.floor(value * resolvedBaseFontSize);
    }

    // Check for any other CSS unit (not supported)
    // This regex matches common CSS units like %, vh, vw, pt, cm, mm, in, etc.
    if (/[a-z%]+$/i.test(trimmed)) {
      return undefined;
    }

    // Handle plain numeric strings
    const value = parseFloat(trimmed);
    return isNaN(value) ? undefined : Math.floor(value);
  }

  return undefined;
};
